import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { DEFAULT_VIDEO_CONSTRAINTS, SELECTED_AUDIO_INPUT_KEY, SELECTED_VIDEO_INPUT_KEY } from '@uplift/constants';
import { useActiveSinkId, useDevices, useMediaStreamTrack, useVideoContext } from '../../../hooks';
import { Modal, ModalContent, SelectItem, SelectOption, SelectRaw } from '@uplift/ui-v2';
import { localStorage } from '@uplift/services';
import { useMemo, useState } from 'react';
import { useVideoStyles } from './styles';

export type SettingsModalProps = {
  onClose: () => void;
  open: boolean;
  isBlurAvailable?: boolean;
};

type DeviceSelectOption = SelectOption & { disabled?: boolean };

const MEDIA_DEFAULT_OPTION: DeviceSelectOption = {
  value: 'default',
  label: 'Default',
  disabled: true,
};

const getMediaOptions = (devices: MediaDeviceInfo[]): DeviceSelectOption[] => {
  if (devices.length === 0) {
    return [ MEDIA_DEFAULT_OPTION ];
  }

  return devices.map((device) => ({ value: device.deviceId, label: device.label }));
};

export const SettingsModal: React.VFC<SettingsModalProps> = (props) => {
  const { classes } = useVideoStyles({});
  const { audioInputDevices, audioOutputDevices, videoInputDevices } = useDevices();
  const { isVideoFilterSupported, isVideoFilterEnabled, toggleVideoFilter, localTracks } = useVideoContext();

  /// *** Audio Input **** ///

  const localAudioTrack = localTracks.find((track) => track.kind === 'audio');
  const localAudioMediaTrackStream = useMediaStreamTrack(localAudioTrack);
  const audioInputDeviceId = localAudioMediaTrackStream?.getSettings()?.deviceId;

  const onAudioInputChange = (deviceId: string) => {
    localStorage.setItem(SELECTED_AUDIO_INPUT_KEY, deviceId);
    localAudioTrack?.restart({ deviceId: { exact: deviceId } });
  };

  /// *** END Audio Input **** ///

  /// *** Audio Output **** ///

  const { activeSinkId: audioOuputDeviceId, setActiveSinkId } = useActiveSinkId();

  const onAudioOutputChange = (deviceId: string) => {
    setActiveSinkId(deviceId);
  };

  /// *** END Audio Output **** ///

  /// *** Video Input *** ///

  const localVideoTrack = localTracks.find((track) => track.kind === 'video');
  const localVideoMediaTrackStream = useMediaStreamTrack(localVideoTrack);
  const [ storedLocalVideoDeviceId, setStoredLocalVideoDeviceId ] = useState(
    localStorage.getItem(SELECTED_VIDEO_INPUT_KEY) || 'default'
  );

  const videoInputDeviceId = localVideoMediaTrackStream?.getSettings?.()?.deviceId || storedLocalVideoDeviceId;

  const onVideoInputChange = (deviceId: string) => {
    // Here we store the device ID in the component state. This is so we can re-render this component display
    // to display the name of the selected device when it is changed while the users camera is off.
    setStoredLocalVideoDeviceId(deviceId);
    localStorage.setItem(SELECTED_VIDEO_INPUT_KEY, deviceId);
    localVideoTrack?.restart({
      ...(DEFAULT_VIDEO_CONSTRAINTS as MediaTrackConstraints),
      deviceId: { exact: deviceId },
    });
    // resets video filter setting
    toggleVideoFilter(true);
  };

  /// *** END Video Input *** ///

  const blurCheckbox = useMemo(() => {
    if (props.isBlurAvailable && isVideoFilterSupported) {
      return (
        <Box pl={1}>
          <FormControlLabel
            label="Background blur"
            control={(
              <Checkbox
                color="secondary"
                onChange={(_e) => toggleVideoFilter()}
                checked={isVideoFilterEnabled}
              />
            )}
          />
        </Box>
      );
    }

    return null;
  }, [ isVideoFilterEnabled, isVideoFilterSupported, props.isBlurAvailable, toggleVideoFilter ]);


  const audioInputOptions = getMediaOptions(audioInputDevices);
  const audioOutputOptions = getMediaOptions(audioOutputDevices);
  const videoInputOptions = getMediaOptions(videoInputDevices);

  const renderDeviceOptions = (options: DeviceSelectOption[]) => {
    return options.map(({ value, label, disabled }) => (
      <SelectItem key={value} value={value} disabled={disabled}>
        {label}
      </SelectItem>
    ));
  };

  return (
    <Modal
      open={props.open}
      title="Settings"
      onClose={props.onClose}
      classes={{
        paper: classes.settingsModalPaper
      }}
    >
      <ModalContent>
        <Box display="flex" flexDirection="column" gap="0.5rem">
          <SelectRaw
            label="Microphone"
            name="microphone"
            onChange={(e) => onAudioInputChange(e.target.value)}
            value={audioInputDeviceId}
          >
            {renderDeviceOptions(audioInputOptions)}
          </SelectRaw>
          <SelectRaw
            label="Camera"
            name="camera"
            onChange={(e) => onVideoInputChange(e.target.value)}
            value={videoInputDeviceId}
          >
            {renderDeviceOptions(videoInputOptions)}
          </SelectRaw>
          <SelectRaw
            label="Speaker"
            name="speaker"
            onChange={(e) => onAudioOutputChange(e.target.value)}
            value={audioOuputDeviceId}
          >
            {renderDeviceOptions(audioOutputOptions)}
          </SelectRaw>
          {blurCheckbox}
        </Box>
      </ModalContent>
    </Modal>
  );
};
